// Session-scoped de-duplication for analytics events, backed by sessionStorage
// so it survives re-renders/remounts but resets per browser session/tab.
//
// markOnce returns true the FIRST time an id is seen for a namespace (and records
// it), false on every subsequent call. Used to fire offer_impression / offer_applied
// at most once per session per offer_id.

const keyFor = (namespace: string): string => `analytics_dedupe:${namespace}`;

const read = (namespace: string): Set<string> => {
  try {
    const raw = sessionStorage.getItem(keyFor(namespace));

    return new Set<string>(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set<string>();
  }
};

export const markOnce = (namespace: string, id: string): boolean => {
  if (!id) {
    return false;
  }

  const seen = read(namespace);

  if (seen.has(id)) {
    return false;
  }

  seen.add(id);

  try {
    sessionStorage.setItem(keyFor(namespace), JSON.stringify([...seen]));
  } catch {
    // sessionStorage unavailable (private mode / SSR) — fall through; the event
    // simply may fire more than once, which is preferable to not firing.
  }

  return true;
};

export const DEDUPE_NAMESPACES = {
  Impression: 'offer_impression',
  Applied: 'offer_applied',
} as const;
